import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
function FadeInUp({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay
  }} data-id="element-228">
      {children}
    </motion.div>;
}
export function MechanismSection() {
  return <section className="w-full py-20 px-4 relative overflow-hidden" style={{
    background: 'radial-gradient(ellipse at 50% 50%, #0f1f3d 0%, #0a1628 70%)'
  }} data-id="element-229">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10 pointer-events-none" style={{
      background: 'var(--color-blue-glow)'
    }} data-id="element-230" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-231">
        <FadeInUp data-id="element-232">
          <div className="text-center mb-14" data-id="element-233">
            <div className="section-divider mb-6" data-id="element-234">
              <span className="ornament" data-id="element-235">⬡</span>
            </div>
            <span className="inline-block font-body text-xs tracking-widest uppercase mb-4 px-3 py-1 rounded-full" style={{
            color: 'var(--color-blue-glow)',
            background: 'rgba(74, 158, 255, 0.1)',
            border: '1px solid rgba(74, 158, 255, 0.3)'
          }} data-id="element-236">
              Premio Nobel de Medicina · Autofagia
            </span>
            <h2 className="font-heading font-700 text-4xl md:text-5xl mb-4" style={{
            color: 'var(--color-cream)'
          }} data-id="element-237">
              El Mecanismo Que Lo Cambia
              <span className="block italic" style={{
              color: 'var(--color-gold)'
            }} data-id="element-238">
                Todo: Bio-Restauración Génesis
              </span>
            </h2>
            <p className="font-body text-lg max-w-2xl mx-auto leading-relaxed" style={{
            color: 'var(--color-cream-dim)'
          }} data-id="element-239">
              Un método de tres pilares que activa el{' '}
              <strong style={{
              color: 'var(--color-cream)'
            }} data-id="element-240">
                "interruptor de la juventud"
              </strong>{' '}
              en tu ADN.
            </p>
          </div>
        </FadeInUp>

        {/* Three pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14" data-id="element-241">
          <FadeInUp delay={0.1} data-id="element-242">
            <div className="rounded-xl p-6 text-center h-full flex flex-col" style={{
            background: 'rgba(15, 31, 61, 0.8)',
            border: '1px solid rgba(74, 158, 255, 0.25)'
          }} data-id="element-243">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{
              background: 'rgba(74, 158, 255, 0.1)',
              border: '1px solid rgba(74, 158, 255, 0.3)'
            }} data-id="element-244">
                🔬
              </div>
              <h3 className="font-heading font-600 text-xl mb-3" style={{
              color: 'var(--color-blue-glow)'
            }} data-id="element-245">
                Autofagia Activada
              </h3>
              <p className="font-body text-sm leading-relaxed flex-1" style={{
              color: 'var(--color-cream-dim)'
            }} data-id="element-246">
                El proceso de reciclaje celular que ganó el Premio Nobel de
                Medicina. Tu cuerpo devora las células dañadas y las reemplaza
                por tejido nuevo y funcional.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.2} data-id="element-247">
            <div className="rounded-xl p-6 text-center h-full flex flex-col relative" style={{
            background: 'rgba(15, 31, 61, 0.8)',
            border: '1px solid rgba(201, 168, 76, 0.4)',
            boxShadow: '0 0 30px rgba(201, 168, 76, 0.1)'
          }} data-id="element-248">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-body font-700 tracking-wider uppercase" style={{
              background: 'var(--color-gold)',
              color: '#0a1628'
            }} data-id="element-249">
                El Núcleo
              </div>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{
              background: 'rgba(201, 168, 76, 0.1)',
              border: '1px solid rgba(201, 168, 76, 0.4)'
            }} data-id="element-250">
                📖
              </div>
              <h3 className="font-heading font-600 text-xl mb-3" style={{
              color: 'var(--color-gold)'
            }} data-id="element-251">
                Nutrición del Génesis
              </h3>
              <p className="font-body text-sm leading-relaxed flex-1" style={{
              color: 'var(--color-cream-dim)'
            }} data-id="element-252">
                Los alimentos de alta densidad nutricional mencionados en los
                textos bíblicos. Aceite de oliva, granada, higos y otros pilares
                para detener la oxidación.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.3} data-id="element-253">
            <div className="rounded-xl p-6 text-center h-full flex flex-col" style={{
            background: 'rgba(15, 31, 61, 0.8)',
            border: '1px solid rgba(74, 124, 89, 0.35)'
          }} data-id="element-254">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{
              background: 'rgba(74, 124, 89, 0.1)',
              border: '1px solid rgba(74, 124, 89, 0.4)'
            }} data-id="element-255">
                🌅
              </div>
              <h3 className="font-heading font-600 text-xl mb-3" style={{
              color: 'var(--color-olive)'
            }} data-id="element-256">
                Ritmos Ancestrales
              </h3>
              <p className="font-body text-sm leading-relaxed flex-1" style={{
              color: 'var(--color-cream-dim)'
            }} data-id="element-257">
                Sincronizamos tus horas de ingesta con los principios de luz y
                descanso ancestrales para obligar a tu cuerpo a regenerarse de
                forma natural.
              </p>
            </div>
          </FadeInUp>
        </div>

        <FadeInUp delay={0.4} data-id="element-258">
          <div className="rounded-xl p-8 text-center" style={{
          background: 'linear-gradient(135deg, rgba(201, 168, 76, 0.08), rgba(74, 158, 255, 0.05))',
          border: '1px solid rgba(201, 168, 76, 0.2)'
        }} data-id="element-259">
            <p className="font-heading text-xl md:text-2xl italic font-400 leading-relaxed" style={{
            color: 'var(--color-cream)'
          }} data-id="element-260">
              "No es una dieta; es una{' '}
              <span style={{
              color: 'var(--color-gold)'
            }} data-id="element-261">
                actualización de tu sistema operativo biológico.
              </span>
              "
            </p>
          </div>
        </FadeInUp>
      </div>
    </section>;
}